/**
 * Pocketbase Service
 * 
 * A centralized service layer for all Pocketbase operations.
 * This abstraction provides consistent error handling, caching,
 * and a clean API for components to interact with Pocketbase.
 */

import PocketBase, { LocalAuthStore } from 'pocketbase';
import config from '../config/pocketbase.config';

/** localStorage key for `pbAdmin` (Zilup admin hub + all admin analytics gates). */
/** localStorage key for `pbAdmin` (Zilup admin hub + admin analytics route gates). */
const ADMIN_AUTH_STORAGE_KEY = 'zilup_pb_admin_auth';

// App user session (records) — separate from admin so the same email can hold both sessions.
const pb = new PocketBase(config.apiUrl);
// Admin dashboard — own auth store + localStorage key; never overwrites app `pb.authStore`.
const pbAdmin = new PocketBase(config.apiUrl, new LocalAuthStore(ADMIN_AUTH_STORAGE_KEY));

// Disable auto-cancellation to prevent concurrent UI-triggered requests from being aborted
// See: https://github.com/pocketbase/js-sdk#auto-cancellation
try {
  pb.autoCancellation(false);
} catch {}
try {
  pbAdmin.autoCancellation(false);
} catch {}

function isAdminAuthModel(model) {
  if (!model || typeof model !== 'object') return false;
  const cn = model.collectionName;
  return cn === '_superusers' || cn === 'admins' || cn === '_admins';
}

// Realtime helpers (prevents "Missing or invalid client id" forever)
async function ensureRealtimeConnected() {
  // js-sdk exposes connect/disconnect on pb.realtime in recent versions.
  // If a stale clientId exists (PB restart), reconnect will mint a new one.
  if (!pb?.realtime) return;
  if (pb.realtime.isConnected === true) return;
  await pb.realtime.connect();
}

async function forceRealtimeReconnect() {
  if (!pb?.realtime) return;
  try {
    if (pb.realtime.isConnected === true) {
      await pb.realtime.disconnect();
    }
  } catch {}
  await pb.realtime.connect();
}

export async function safeSubscribe(collection, topic, callback) {
  try {
    await ensureRealtimeConnected();
    const unsub = await pb.collection(collection).subscribe(topic, callback);
    // Ensure calling unsubscribe never throws "Missing or invalid client id"
    return async () => {
      try {
        return await unsub();
      } catch (error) {
        const msg = String(error?.message || '');
        if (/client id/i.test(msg)) {
          // Stale clientId during teardown/hot-reload; ignore.
          return;
        }
        throw error;
      }
    };
  } catch (error) {
    const msg = String(error?.message || '');
    if (/client id/i.test(msg)) {
      await forceRealtimeReconnect();
      const unsub = await pb.collection(collection).subscribe(topic, callback);
      return async () => {
        try {
          return await unsub();
        } catch (err) {
          const m = String(err?.message || '');
          if (/client id/i.test(m)) return;
          throw err;
        }
      };
    }
    throw error;
  }
}

// Initialize PocketBase auth store from localStorage if available
let isInitializingAuth = false;
try {
  const storedAuth = localStorage.getItem(config.auth.tokenStorageKey);
  if (storedAuth) {
    isInitializingAuth = true;
    pb.authStore.loadFromCookie(storedAuth);
    isInitializingAuth = false;
  }
} catch (error) {
  console.error('Error loading auth from localStorage:', error);
  isInitializingAuth = false;
}

// Listen for auth store changes and persist to localStorage
let authChangeTimeout = null;
pb.authStore.onChange((token, model) => {
  // Skip if we're initializing to prevent loops
  if (isInitializingAuth) {
    return;
  }
  
  // Debounce rapid auth changes
  if (authChangeTimeout) {
    clearTimeout(authChangeTimeout);
  }
  
  authChangeTimeout = setTimeout(() => {
    if (token && model) {
      // `exportToCookie()` stringifies `record` without a replacer — DOM / React-internal
      // graphs (e.g. SVGElement ↔ FiberNode) throw and can surface as unrelated failures.
      try {
        localStorage.setItem(config.auth.tokenStorageKey, pb.authStore.exportToCookie());
      } catch (e) {
        console.warn(
          'Could not persist pb_auth cookie (non-serializable model); session may not survive full page reload.',
          e?.message || e
        );
      }
      // Also store user data for compatibility
      // PocketBase models can sometimes contain circular references (eg. via `expand`).
      // Avoid breaking auth persistence on refresh by safely serializing.
      const seen = new WeakSet();
      const safeModelJson = JSON.stringify(model, (key, value) => {
        if (key === 'expand') return undefined;
        if (typeof Element !== 'undefined' && value instanceof Element) return undefined;
        if (typeof value === 'object' && value !== null) {
          if (seen.has(value)) return undefined;
          seen.add(value);
        }
        return value;
      });
      localStorage.setItem(config.auth.userStorageKey, safeModelJson);
    } else {
      // Clear stored data when logged out
      localStorage.removeItem(config.auth.tokenStorageKey);
      localStorage.removeItem(config.auth.userStorageKey);
    }
  }, 50); // 50ms debounce
});

// Initialize cache storage
const cache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds

// Auth refresh state to prevent race conditions
let refreshAuthPromise = null;

/**
 * Auth record id for the signed-in users collection.
 * Prefer pb.authStore.model.id when the session is valid so PATCH /records/:id never uses a stale
 * or missing React id (which becomes the string "undefined" and yields 404).
 */
function resolveAuthenticatedRecordId(passedUserId) {
  if (pb.authStore.isValid && pb.authStore.model?.id != null) {
    const id = pb.authStore.model.id;
    return typeof id === 'string' ? id : String(id);
  }
  if (passedUserId != null && passedUserId !== '' && String(passedUserId) !== 'undefined') {
    return String(passedUserId);
  }
  return null;
}

function socialLinksOwnerFilter(userId) {
  return `user_id = "${userId}" || user_id.id = "${userId}"`;
}

/**
 * Error handler for Pocketbase operations
 * @param {Error} error - The error object
 * @param {string} operation - The operation that failed
 * @returns {Object} Standardized error object
 */
const handleError = (error, operation) => {
  console.error(`PocketBase ${operation} error:`, error);
  
  // Default error response
  const errorResponse = {
    success: false,
    message: 'An unexpected error occurred',
    code: 'UNKNOWN_ERROR',
    details: null,
    operation
  };
  
  // Treat PocketBase auto-cancellation (Abort) as a non-fatal, ignorable case
  // See: PocketBase JS SDK "Auto cancellation" docs
  if (
    (error && (error.isAbort === true || error.name === 'AbortError')) ||
    (typeof error?.message === 'string' && /autocancel|abort|cancell?ed/i.test(error.message))
  ) {
    return {
      success: false,
      message: 'Request canceled',
      code: 'ABORTED',
      details: null,
      operation,
      aborted: true
    };
  }

  // Handle PocketBase specific errors
  if (error.status) {
    errorResponse.code = `PB_${error.status}`;
    
    // Provide more specific error messages based on status code and operation
    switch (error.status) {
      case 400:
        if (operation === 'login') {
          errorResponse.message = 'Invalid email/username or password. Please check your credentials.';
        } else {
          errorResponse.message = 'Invalid request. Please check your input and try again.';
        }
        break;
      case 401:
        errorResponse.message = 'Authentication required. Please log in to continue.';
        break;
      case 403:
        if (operation === 'login') {
          errorResponse.message = 'Account access denied. Please verify your email or contact support.';
        } else {
          errorResponse.message = 'Access denied. You don\'t have permission to perform this action.';
        }
        break;
      case 404:
        if (operation === 'updateProfile' || operation === 'updateSocialLink' || operation === 'deleteSocialLink') {
          errorResponse.message =
            'Could not save your changes (profile not found). Try signing out and signing in again.';
        } else {
          errorResponse.message = 'The requested resource was not found.';
        }
        break;
      case 422:
        errorResponse.message = 'Validation failed. Please check your input.';
        break;
      case 429:
        errorResponse.message = 'Too many requests. Please wait a moment and try again.';
        break;
      case 500:
      case 502:
      case 503:
        errorResponse.message = 'Server error. Please try again in a moment.';
        break;
      default:
        errorResponse.message = 'An unexpected error occurred. Please try again.';
    }
    
    // Extract field validation errors if available
    if (error.response && error.response.data) {
      // Use the server message if it's more specific
      if (error.response.data.message && error.response.data.message !== 'validation failure') {
        errorResponse.message = error.response.data.message;
      }
      errorResponse.details = error.response.data.data;
    } else if (error.message && error.message !== 'validation failure') {
      errorResponse.message = error.message;
    }
  } else if (error.message) {
    errorResponse.message = error.message;
  }
  
  return errorResponse;
};

/**
 * Get cached data or fetch from API
 * @param {string} cacheKey - Unique key for the cache entry
 * @param {Function} fetchFn - Function to fetch data if not cached
 * @returns {Promise<any>} The cached or fetched data
 */
const getCachedOrFetch = async (cacheKey, fetchFn) => {
  // Check if we have a valid cache entry
  if (cache.has(cacheKey)) {
    const { data, timestamp } = cache.get(cacheKey);
    const isExpired = Date.now() - timestamp > CACHE_DURATION;
    
    if (!isExpired) {
      return data;
    }
  }
  
  // Fetch fresh data
  const data = await fetchFn();
  
  // Cache the result
  cache.set(cacheKey, {
    data,
    timestamp: Date.now()
  });
  
  return data;
};

/**
 * Clear cache entries that match a prefix
 * @param {string} prefix - Cache key prefix to match
 */
const clearCacheByPrefix = (prefix) => {
  for (const key of cache.keys()) {
    if (key.startsWith(prefix)) {
      cache.delete(key);
    }
  }
};

/**
 * Authentication service methods
 */
const authService = {
  /**
   * Register a new user
   * @param {Object} userData - User registration data
   * @returns {Promise<Object>} Registration result
   */
  async register(userData) {
    try {
      console.log('📝 Starting user registration for:', userData.username);
      console.log('🌐 Registration domain:', window.location.hostname);
      
      // Validate required fields
      if (!userData.email || userData.email === 'undefined' || userData.email.trim() === '') {
        throw {
          type: 'validation_error',
          message: 'Email is required and cannot be empty',
          details: { email: 'Email is required' }
        };
      }
      
      if (!userData.username || userData.username.trim() === '') {
        throw {
          type: 'validation_error',
          message: 'Username is required and cannot be empty',
          details: { username: 'Username is required' }
        };
      }
      
      // Extract collection name and social links from userData
      const collection = userData.collection || config.collections.users;
      const userDataCopy = { ...userData };
      const socialLinks = userDataCopy.socialLinks || [];
      delete userDataCopy.collection;
      delete userDataCopy.socialLinks; // Remove social links from user data
      
      // Use FormData to support file uploads and optional fields
      const formData = new FormData();
      formData.append('email', userDataCopy.email.trim());
      formData.append('password', userDataCopy.password);
      formData.append('passwordConfirm', userDataCopy.passwordConfirm || userDataCopy.password);
      formData.append('name', userDataCopy.name);
      formData.append('username', userDataCopy.username);
  
      // Optional fields: photo (File), role, location, introduction, hideCard
      if (userDataCopy.photo) {
        formData.append('photo', userDataCopy.photo);
      }
      if (userDataCopy.role) {
        formData.append('role', userDataCopy.role);
      }
      if (userDataCopy.location) {
        formData.append('location', userDataCopy.location);
      }
      if (userDataCopy.introduction) {
        formData.append('introduction', userDataCopy.introduction);
      }
      if (userDataCopy.hideCard !== undefined) {
        formData.append('hideCard', userDataCopy.hideCard);
      }
      
      // Create the user record
      const record = await pb.collection(collection).create(formData);
      console.log('✅ User record created:', record.id);
      
      // Authenticate the user immediately after registration
      const authData = await pb.collection(collection).authWithPassword(
        userDataCopy.email,
        userDataCopy.password
      );
      
      console.log('🔐 User authenticated after registration:', authData.record.username);
      console.log('🎫 Auth token received:', !!authData.token);
      
      // Create social links if provided
      if (socialLinks && socialLinks.length > 0) {
        console.log('🔗 Creating social links...');
        for (const link of socialLinks) {
          try {
            const linkData = {
              user_id: record.id,
              platform: link.platform,
              url: link.url,
              username: link.username || '',
              isPublic: true
            };
            
            await pb.collection(config.collections.socialLinks).create(linkData);
          } catch (linkError) {
            console.error('❌ Error creating social link:', linkError);
            // Continue with other links even if one fails
          }
        }
      }
      
      // Note: User data is automatically stored via pb.authStore.onChange listener
      console.log('💾 User data stored via authStore listener');
      
      console.log('🎉 Registration completed successfully');
      return {
        success: true,
        user: authData.record
      };
    } catch (error) {
      console.error('❌ Registration error details:', error);
      return handleError(error, 'register');
    }
  },
  
  /**
   * Login a user
   * @param {string} identifier - Email or username
   * @param {string} password - User password
   * @returns {Promise<Object>} Login result
   */
  async login(identifier, password) {
    try {
      // Validate input
      if (!identifier || !password) {
        throw {
          type: 'validation_error',
          message: 'Email/username and password are required',
          details: {
            identifier: !identifier ? 'Email/username is required' : null,
            password: !password ? 'Password is required' : null
          }
        };
      }
      
      // Determine if identifier is email or username
      const isEmail = identifier.includes('@');
      const collection = config.collections.users;
      
      console.log('Attempting login with:', { identifier: identifier, isEmail, collection });
      
      // Authenticate with PocketBase
      const authData = await pb.collection(collection).authWithPassword(
        identifier,
        password
      );
      
      console.log('Login successful:', { userId: authData.record?.id, username: authData.record?.username });
      
      // Note: User data is automatically stored via pb.authStore.onChange listener
      
      return {
        success: true,
        user: authData.record
      };
    } catch (error) {
      console.error('Login error details:', {
        status: error.status,
        message: error.message,
        response: error.response,
        data: error.response?.data,
        identifier: identifier
      });
      
      // Handle specific authentication errors
      if (error.status === 400) {
        return {
          success: false,
          message: 'Email or password incorrect. Please try again',
          code: 'INVALID_CREDENTIALS',
          operation: 'login'
        };
      }
      
      if (error.status === 403) {
        return {
          success: false,
          message: 'Account access denied. Please verify your email or contact support.',
          code: 'ACCESS_DENIED',
          operation: 'login'
        };
      }
      
      return handleError(error, 'login');
    }
  },
  
  /**
   * Logout the current user
   * @returns {Promise<Object>} Logout result
   */
  async logout() {
    try {
      // Clear PocketBase auth store (this will trigger the onChange listener)
      pb.authStore.clear();
      
      // Clear user-specific cache
      clearCacheByPrefix('user_');
      
      return {
        success: true,
        message: 'Logged out successfully'
      };
    } catch (error) {
      return handleError(error, 'logout');
    }
  },
  
  /**
   * Get the current authenticated user
   * @returns {Object|null} Current user or null if not authenticated
   */
  getCurrentUser() {
    try {
      console.log('🔍 Getting current user...');
      console.log('🔐 PB auth valid:', pb.authStore.isValid);
      console.log('🎫 PB token exists:', !!pb.authStore.token);
      console.log('👤 PB model exists:', !!pb.authStore.model);
      
      // First check if PocketBase auth store is valid
      if (pb.authStore.isValid) {
        console.log('✅ Valid PB auth found:', pb.authStore.model?.username);
        return pb.authStore.model;
      }
      
      // If not valid, try to restore from localStorage
      const storedUser = localStorage.getItem(config.auth.userStorageKey);
      console.log('💾 Stored user exists:', !!storedUser);
      
      if (storedUser) {
        const userData = JSON.parse(storedUser);
        // Check if we have a valid user object
        if (userData && userData.id) {
          console.log('📦 Parsed stored user:', userData.username);
          return userData;
        }
      }
      
      console.log('❌ No current user found');
      return null;
    } catch (error) {
      console.error('❌ Error retrieving user from localStorage:', error);
      return null;
    }
  },
  
  /**
   * Check if user is authenticated
   * @returns {boolean} True if user is authenticated
   */
  isAuthenticated() {
    return pb.authStore.isValid;
  },

  /**
   * Refresh the authentication token and update stored user
   * @returns {Promise<Object>} Refresh result
   */
  async refreshAuth() {
    // If there's already a refresh in progress, return the existing promise
    if (refreshAuthPromise) {
      return refreshAuthPromise;
    }
    
    // Create and store the refresh promise
    refreshAuthPromise = (async () => {
      try {
        // First check if we have a valid auth session
        if (!pb.authStore.isValid) {
          // Try to restore from localStorage and authenticate
          try {
            const storedUser = localStorage.getItem(config.auth.userStorageKey);
            if (storedUser) {
              const userData = JSON.parse(storedUser);
              if (userData && userData.email) {
                // We have stored user data but no valid session
                // This means the session expired, return failure to trigger re-login
                return {
                  success: false,
                  message: 'Authentication session expired'
                };
              }
            }
          } catch (parseError) {
            console.error('Error parsing stored user data:', parseError);
          }
          
          return {
            success: false,
            message: 'No valid authentication session'
          };
        }

        // Check if this is an admin session - if so, skip refresh
        if (pb.authStore.model && pb.authStore.model.collectionName) {
          const collectionName = pb.authStore.model.collectionName;
          if (collectionName === 'admins' || collectionName === '_admins' || 
              collectionName?.includes('admin')) {
            // For admin sessions, just return success without refreshing
            return {
              success: true,
              user: pb.authStore.model
            };
          }
        }

        // Perform PocketBase auth refresh on the users collection (only for regular users)
        await pb.collection(config.collections.users).authRefresh();

        // Note: Updated user data is automatically stored via pb.authStore.onChange listener

        return {
          success: true,
          user: pb.authStore.model
        };
      } catch (error) {
        // If refresh fails, clear stored data
        localStorage.removeItem(config.auth.userStorageKey);
        return handleError(error, 'refreshAuth');
      } finally {
        // Clear the promise when done
        refreshAuthPromise = null;
      }
    })();
    
    return refreshAuthPromise;
  },

  /**
   * Delete user account
   * @param {string} password - Current user's password for confirmation
   * @returns {Promise<Object>} Delete result
   */
  async deleteAccount(password) {
    try {
      if (!pb.authStore.isValid || !pb.authStore.model) {
        return {
          success: false,
          message: 'No authenticated user'
        };
      }

      const userId = pb.authStore.model.id;
      const userEmail = pb.authStore.model.email;

      // Verify password first
      try {
        await pb.collection(config.collections.users).authWithPassword(
          userEmail,
          password
        );
      } catch (error) {
        return {
          success: false,
          message: 'Invalid password'
        };
      }

      // Send farewell email before deletion
      try {
        await this.sendFarewellEmail(userEmail);
      } catch (emailError) {
        console.warn('Failed to send farewell email:', emailError);
        // Continue with deletion even if email fails
      }

      // Delete user record
      await pb.collection(config.collections.users).delete(userId);

      // Clear auth store and local storage
      pb.authStore.clear();
      localStorage.removeItem(config.auth.userStorageKey);

      // Clear user-specific cache
      clearCacheByPrefix('user_');

      return {
        success: true,
        message: 'Account deleted successfully'
      };
    } catch (error) {
      return handleError(error, 'deleteAccount');
    }
  },

  /**
   * Request email verification
   * @param {string} email - User email to verify
   * @returns {Promise<Object>} Verification request result
   */
  async requestEmailVerification(email) {
    try {
      if (!email) {
        throw {
          type: 'validation_error',
          message: 'Email is required'
        };
      }

      await pb.collection(config.collections.users).requestVerification(email);

      return {
        success: true,
        message: 'Verification email sent successfully'
      };
    } catch (error) {
      return handleError(error, 'requestEmailVerification');
    }
  },

  /**
   * Confirm email verification
   * @param {string} token - Verification token from email
   * @returns {Promise<Object>} Verification confirmation result
   */
  async confirmEmailVerification(token) {
    try {
      if (!token) {
        throw {
          type: 'validation_error',
          message: 'Verification token is required'
        };
      }

      await pb.collection(config.collections.users).confirmVerification(token);

      return {
        success: true,
        message: 'Email verified successfully'
      };
    } catch (error) {
      return handleError(error, 'confirmEmailVerification');
    }
  },

  /**
   * Request password reset
   * @param {string} email - User email for password reset
   * @returns {Promise<Object>} Password reset request result
   */
  async requestPasswordReset(email) {
    try {
      if (!email) {
        throw {
          type: 'validation_error',
          message: 'Email is required'
        };
      }

      await pb.collection(config.collections.users).requestPasswordReset(email);

      return {
        success: true,
        message: 'Password reset email sent successfully'
      };
    } catch (error) {
      return handleError(error, 'requestPasswordReset');
    }
  },

  /**
   * Confirm password reset
   * @param {string} token - Reset token from email
   * @param {string} password - New password
   * @param {string} passwordConfirm - Password confirmation
   * @returns {Promise<Object>} Password reset confirmation result
   */
  async confirmPasswordReset(token, password, passwordConfirm) {
    try {
      console.log('🔄 Starting password reset confirmation...');
      console.log('Token received:', token ? 'Yes' : 'No');
      console.log('Password length:', password ? password.length : 0);
      console.log('Password confirm length:', passwordConfirm ? passwordConfirm.length : 0);

      if (!token || !password || !passwordConfirm) {
        throw {
          type: 'validation_error',
          message: 'All fields are required'
        };
      }

      if (password !== passwordConfirm) {
        throw {
          type: 'validation_error',
          message: 'Passwords do not match'
        };
      }

      console.log('🔄 Calling PocketBase confirmPasswordReset...');
      const result = await pb.collection(config.collections.users).confirmPasswordReset(
        token,
        password,
        passwordConfirm
      );
      
      console.log('✅ PocketBase confirmPasswordReset result:', result);

      return {
        success: true,
        message: 'Password reset successfully'
      };
    } catch (error) {
      console.error('❌ Password reset error:', error);
      console.error('Error details:', {
        status: error?.status,
        response: error?.response,
        data: error?.data,
        message: error?.message
      });
      return handleError(error, 'confirmPasswordReset');
    }
  },

  /**
   * Send farewell email (placeholder for custom implementation)
   * @param {string} email - User email
   * @returns {Promise<Object>} Email send result
   */
  async sendFarewellEmail(email) {
    try {
      // This would typically be handled by a backend hook
      // For now, we'll log it and return success
      console.log(`Farewell email would be sent to: ${email}`);
      
      // In a real implementation, you might call a custom endpoint
      // or use a third-party email service
      
      return {
        success: true,
        message: 'Farewell email sent'
      };
    } catch (error) {
      return handleError(error, 'sendFarewellEmail');
    }
  }
};

/**
 * User service methods
 */
const userService = {
  /**
   * Get user profile by username
   * @param {string} username - Username to lookup
   * @returns {Promise<Object>} API response with user data
   */
  async getUserByUsername(username) {
    try {
      const cacheKey = `user_${username}`;
      
      return await getCachedOrFetch(cacheKey, async () => {
        const records = await pb.collection(config.collections.users).getList(1, 1, {
          filter: `username = "${username}"`,
          expand: 'social_links'
        });
        
        if (records.items.length === 0) {
          throw new Error('User not found');
        }
        
        return {
          success: true,
          user: records.items[0]
        };
      });
    } catch (error) {
      return handleError(error, 'getUserByUsername');
    }
  },
  
  /**
   * Update user profile
   * @param {string} userId - User ID to update
   * @param {Object} userData - Updated user data
   * @returns {Promise<Object>} API response
   */
  async updateProfile(userId, userData) {
    try {
      const recordId = resolveAuthenticatedRecordId(userId);
      if (!recordId) {
        return {
          success: false,
          message: 'Your session is invalid. Please sign out and sign in again.',
          code: 'INVALID_SESSION',
          operation: 'updateProfile'
        };
      }

      // Use FormData to support file uploads
      const formData = new FormData();
      
      // Add all fields to FormData
      Object.keys(userData).forEach(key => {
        if (userData[key] !== undefined) {
          // For photo field, explicitly handle null to clear the file
          if (key === 'photo' && userData[key] === null) {
            formData.append(key, '');
          } else if (userData[key] !== null) {
            formData.append(key, userData[key]);
          }
        }
      });
      
      const record = await pb.collection(config.collections.users).update(recordId, formData);
      
      // Clear user cache
      clearCacheByPrefix(`user_${record.username}`);
      
      return {
        success: true,
        user: record
      };
    } catch (error) {
      return handleError(error, 'updateProfile');
    }
  },
  
  /**
   * Check if email is available
   * @param {string} email - Email to check
   * @returns {Promise<Object>} API response with availability status
   */
  async checkEmailAvailability(email) {
    try {
      if (!email || typeof email !== 'string') {
        return { success: false, message: 'Invalid email provided' };
      }

      const trimmedEmail = email.trim().toLowerCase();
      
      // Basic email format validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(trimmedEmail)) {
        return { success: false, message: 'Invalid email format' };
      }

      const records = await pb.collection(config.collections.users).getList(1, 1, {
        filter: `email = "${trimmedEmail}"`
      });

      return { 
        success: true, 
        available: records.items.length === 0,
        message: records.items.length > 0 ? 'Email is taken' : 'Email is available'
      };
    } catch (error) {
      return handleError(error, 'checkEmailAvailability');
    }
  },

  /**
   * Get user by ID
   * @param {string} userId - User ID to lookup
   * @returns {Promise<Object>} API response with user data
   */
  async getUserById(userId) {
    try {
      const cacheKey = `user_id_${userId}`;
      
      return await getCachedOrFetch(cacheKey, async () => {
        const record = await pb.collection(config.collections.users).getOne(userId);
        
        return {
          success: true,
          user: record
        };
      });
    } catch (error) {
      return handleError(error, 'getUserById');
    }
  },
  
  /**
   * Check if username is available
   * @param {string} username - Username to check
   * @returns {Promise<Object>} API response with availability status
   */
  async checkUsernameAvailability(username) {
    try {
      if (!username || typeof username !== 'string') {
        return { success: false, message: 'Invalid username provided' };
      }

      const trimmedUsername = username.trim().toLowerCase();
      
      // Basic username validation
      if (trimmedUsername.length < 3) {
        return { success: false, message: 'Username must be at least 3 characters long' };
      }

      const records = await pb.collection(config.collections.users).getList(1, 1, {
        filter: `username = "${trimmedUsername}"`
      });

      return { 
        success: true, 
        available: records.items.length === 0,
        message: records.items.length > 0 ? 'Username is taken' : 'Username is available'
      };
    } catch (error) {
      return handleError(error, 'checkUsernameAvailability');
    }
  }
};

/**
 * Social Links service methods
 */
const socialLinksService = {
  /**
   * Drop cached social links for this user (e.g. after filter/schema fixes or before settings load).
   */
  clearSocialLinksCache(userId) {
    if (!userId) return;
    clearCacheByPrefix(`social_links_${userId}`);
  },

  /**
   * Get social links for a user
   * @param {string} userId - User ID to get links for
   * @returns {Promise<Object>} API response with social links
   */
  async getSocialLinks(userId, options = {}) {
    try {
      const asAdmin = !!options.asAdmin;
      const client = asAdmin ? pbAdmin : pb;
      const cacheKey = asAdmin ? `social_links_admin_${userId}` : `social_links_${userId}`;
      
      return await getCachedOrFetch(cacheKey, async () => {
        const records = await client.collection(config.collections.socialLinks).getFullList({
          filter: socialLinksOwnerFilter(userId),
          sort: 'created'
        });
        
        return {
          success: true,
          links: records
        };
      });
    } catch (error) {
      return handleError(error, 'getSocialLinks');
    }
  },
  
  /**
   * Create a new social link
   * @param {Object} linkData - Social link data
   * @returns {Promise<Object>} API response
   */
  async createSocialLink(linkData) {
    try {
      const record = await pb.collection(config.collections.socialLinks).create(linkData);
      
      // Clear social links cache for this user
      clearCacheByPrefix(`social_links_${linkData.user_id}`);
      
      return {
        success: true,
        link: record
      };
    } catch (error) {
      return handleError(error, 'createSocialLink');
    }
  },
  
  /**
   * Update a social link
   * @param {string} linkId - Link ID to update
   * @param {Object} linkData - Updated link data
   * @returns {Promise<Object>} API response
   */
  async updateSocialLink(linkId, linkData) {
    try {
      const record = await pb.collection(config.collections.socialLinks).update(linkId, linkData);
      
      // Clear social links cache for this user
      clearCacheByPrefix(`social_links_${linkData.user_id}`);
      
      return {
        success: true,
        link: record
      };
    } catch (error) {
      return handleError(error, 'updateSocialLink');
    }
  },
  
  /**
   * Delete a social link
   * @param {string} linkId - Link ID to delete
   * @param {string} userId - User ID for cache clearing
   * @returns {Promise<Object>} API response
   */
  async deleteSocialLink(linkId, userId) {
    try {
      await pb.collection(config.collections.socialLinks).delete(linkId);
      
      // Clear social links cache for this user
      clearCacheByPrefix(`social_links_${userId}`);
      
      return {
        success: true,
        message: 'Social link deleted successfully'
      };
    } catch (error) {
      return handleError(error, 'deleteSocialLink');
    }
  },

  /**
   * Sync user's social links to the server (create/update/delete)
   * @param {string} userId - User ID owning the links
   * @param {Array} newLinks - Array of link objects { platform, url, username }
   * @returns {Promise<Object>} Summary of changes
   */
  async syncSocialLinks(userId, newLinks = []) {
    try {
      // Fetch existing links directly (ignore cache to get freshest state)
      const existing = await pb.collection(config.collections.socialLinks).getFullList({
        filter: socialLinksOwnerFilter(userId),
        sort: 'created'
      });

      // Normalize helpers
      const normalize = (str) => (str || '').trim().toLowerCase();

      // Map existing by platform
      const existingByPlatform = new Map();
      for (const rec of existing) {
        existingByPlatform.set(normalize(rec.platform), rec);
      }

      // Deduplicate and normalize new links by platform
      const newByPlatform = new Map();
      for (const link of Array.isArray(newLinks) ? newLinks : []) {
        const platformKey = normalize(link.platform);
        if (!platformKey) continue;
        const username = (link.username || '').toString();
        const url = (link.url || '').toString();
        // Only keep entries that have a URL
        if (!url.trim()) continue;
        newByPlatform.set(platformKey, {
          platform: link.platform,
          url,
          username
        });
      }

      let created = 0, updated = 0, deleted = 0;

      // Create or update
      for (const [platformKey, link] of newByPlatform.entries()) {
        const existingRec = existingByPlatform.get(platformKey);
        const payload = {
          user_id: userId,
          platform: link.platform,
          url: link.url,
          username: link.username || '',
          isPublic: true
        };

        if (existingRec) {
          // Update only if something changed
          const needsUpdate = (existingRec.url !== payload.url) || (existingRec.username !== payload.username) || (existingRec.platform !== payload.platform);
          if (needsUpdate) {
            await socialLinksService.updateSocialLink(existingRec.id, payload);
            updated++;
          }
        } else {
          await socialLinksService.createSocialLink(payload);
          created++;
        }
      }

      // Delete removed platforms
      for (const [platformKey, rec] of existingByPlatform.entries()) {
        if (!newByPlatform.has(platformKey)) {
          await socialLinksService.deleteSocialLink(rec.id, userId);
          deleted++;
        }
      }

      return {
        success: true,
        summary: { created, updated, deleted }
      };
    } catch (error) {
      return handleError(error, 'syncSocialLinks');
    }
  }
};


/**
 * Favorites service methods (per-record schema)
 * Collection `user_favs` fields:
 * - `user_id` (relation to users)
 * - `fav_user_id` (relation to users)
 * Each favorite is a separate record. This avoids the broken `favs` array.
 */
const favoritesService = {
  /**
   * Get favorites for a user
   * @param {string} userId - The owner user ID
   * @returns {Promise<Object>} API response with expanded users if available
   */
  async getFavorites(userId) {
    try {
      const records = await pb.collection(config.collections.userFavs).getFullList({
        filter: `user_id = "${userId}" || user_id.id = "${userId}"`,
        expand: 'fav_user_id',
        $autoCancel: false
      });
      const expandedUsers = records
        .map(r => (r.expand && r.expand.fav_user_id) ? r.expand.fav_user_id : null)
        .filter(Boolean);
      if (expandedUsers.length > 0) {
        return { success: true, users: expandedUsers, record: null };
      }
      // Fallback: batch fetch users by fav_user_id when expand isn't available.
      // Using a filtered list request avoids 404s from per-record getOne and
      // returns only the records the current auth context can view.
      const ids = records
        .flatMap(r => Array.isArray(r.fav_user_id) ? r.fav_user_id : [r.fav_user_id])
        .filter(Boolean);
      const uniqueIds = Array.from(new Set(ids));

      if (uniqueIds.length === 0) {
        return { success: true, users: [], record: null };
      }

      const orFilter = uniqueIds.map(id => `id = "${id}"`).join(' || ');
      const list = await pb.collection(config.collections.users).getList(1, uniqueIds.length, {
        filter: orFilter,
        $autoCancel: false
      });
      const fetchedUsers = list.items || [];
      return { success: true, users: fetchedUsers, record: null };
    } catch (error) {
      return handleError(error, 'getFavorites');
    }
  },

  /**
   * Check if a user is favorited by another user
   * @param {string} userId - The owner user ID
   * @param {string} favUserId - The target user ID
   * @returns {Promise<Object>} API response with status
   */
  async isFavorite(userId, favUserId) {
    try {
      const perRecord = await pb.collection(config.collections.userFavs).getList(1, 1, {
        filter: `user_id = "${userId}" && fav_user_id = "${favUserId}"`,
        $autoCancel: false
      });
      return { success: true, isFavorite: perRecord.items.length > 0 };
    } catch (error) {
      return handleError(error, 'isFavorite');
    }
  },

  /**
   * Add a favorite user to `favs` array (create or update record)
   */
  async addFavorite(userId, favUserId) {
    try {
      const exists = await pb.collection(config.collections.userFavs).getList(1, 1, {
        filter: `user_id = "${userId}" && fav_user_id = "${favUserId}"`,
        $autoCancel: false
      });
      if (exists.items.length > 0) {
        return { success: true, record: exists.items[0], message: 'Already favorited' };
      }
      const created = await pb.collection(config.collections.userFavs).create({
        user_id: userId,
        fav_user_id: favUserId
      }, { $autoCancel: false });
      clearCacheByPrefix(`favorites_${userId}`);
      return { success: true, record: created };
    } catch (error) {
      return handleError(error, 'addFavorite');
    }
  },

  /**
   * Remove a favorite user from `favs` array
   */
  async removeFavorite(userId, favUserId) {
    try {
      const perRecord = await pb.collection(config.collections.userFavs).getList(1, 1, {
        filter: `user_id = "${userId}" && fav_user_id = "${favUserId}"`,
        $autoCancel: false
      });
      if (perRecord.items.length === 0) {
        return { success: true, message: 'Not favorited' };
      }
      await pb.collection(config.collections.userFavs).delete(perRecord.items[0].id, { $autoCancel: false });
      clearCacheByPrefix(`favorites_${userId}`);
      return { success: true };
    } catch (error) {
      return handleError(error, 'removeFavorite');
    }
  }
  ,
  /**
   * Get favorites count for a user
   * @param {string} userId - The owner user ID
   * @returns {Promise<Object>} API response with count
   */
  async getFavoritesCount(userId) {
    try {
      const list = await pb.collection(config.collections.userFavs).getList(1, 1, {
        filter: `user_id = "${userId}" || user_id.id = "${userId}"`,
        $autoCancel: false
      });
      const total = list?.totalItems ?? (Array.isArray(list?.items) ? list.items.length : 0);
      return { success: true, count: total };
    } catch (error) {
      return handleError(error, 'getFavoritesCount');
    }
  }
};


/**
 * Admin Service
 * Functions for admin operations and user management
 */
const adminService = {
  /**
   * Admin login
   * @param {string} email - Admin email
   * @param {string} password - Admin password
   * @returns {Promise<Object>} Login result
   */
  async adminLogin(email, password) {
    try {
      const authData = await pbAdmin.admins.authWithPassword(email, password);
      
      const admin = authData.record || authData.admin || pbAdmin.authStore.model;
      const token = authData.token || pbAdmin.authStore.token;
      
      if (!admin) {
        throw new Error('Admin authentication failed - no admin data received');
      }
      
      // Legacy keys — remove so old restore paths cannot clear app session
      try {
        localStorage.removeItem('admin_auth');
        localStorage.removeItem('admin_token');
      } catch {}

      return {
        success: true,
        admin: admin,
        token: token
      };
    } catch (error) {
      return handleError(error, 'adminLogin');
    }
  },

  /**
   * Check if admin is authenticated
   * @returns {boolean} True if admin is authenticated
   */
  isAdminAuthenticated() {
    try {
      return !!(pbAdmin.authStore.isValid && isAdminAuthModel(pbAdmin.authStore.model));
    } catch (error) {
      console.error('Error checking admin auth:', error);
      return false;
    }
  },

  /**
   * Admin logout
   */
  adminLogout() {
    try {
      pbAdmin.authStore.clear();
      try {
        localStorage.removeItem('admin_auth');
        localStorage.removeItem('admin_token');
        localStorage.removeItem(ADMIN_AUTH_STORAGE_KEY);
      } catch {}
      return { success: true };
    } catch (error) {
      return handleError(error, 'adminLogout');
    }
  },

  /**
   * Verify admin authentication with server
   * @returns {Promise<boolean>} True if admin auth is valid
   */
  async verifyAdminAuth() {
    try {
      if (!pbAdmin.authStore.isValid || !isAdminAuthModel(pbAdmin.authStore.model)) {
        return false;
      }
      await pbAdmin.collection(config.collections.users).getList(1, 1, { $autoCancel: false });
      return true;
    } catch (error) {
      console.log('Admin auth verification failed:', error);
      this.adminLogout();
      return false;
    }
  },

  /**
   * Get all users for admin panel
   * @returns {Promise<Object>} API response with all users
   */
  async getAllUsers() {
    try {
      const records = await pbAdmin.collection('uud_onboard').getFullList({
        sort: '-created',
        $autoCancel: false
        // Fetch all fields for UserCard component
      });
      
      return { success: true, data: records };
    } catch (error) {
      return handleError(error, 'getAllUsers');
    }
  },

  /**
   * Check if email is available (admin version - checks against all users)
   * @param {string} email - Email to check
   * @returns {Promise<Object>} API response with availability status
   */
  async checkEmailAvailabilityAdmin(email) {
    try {
      if (!email || typeof email !== 'string') {
        return { success: false, message: 'Invalid email provided' };
      }

      const trimmedEmail = email.trim().toLowerCase();
      
      // Basic email format validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(trimmedEmail)) {
        return { success: false, message: 'Invalid email format' };
      }

      // Get all users and check locally
      const allUsersResponse = await this.getAllUsers();
      if (!allUsersResponse.success) {
        return allUsersResponse;
      }

      const users = allUsersResponse.data;
      const emailExists = users.some(user => 
        user.email && 
        user.email !== 'undefined' && 
        user.email.trim().toLowerCase() === trimmedEmail
      );

      return { 
        success: true, 
        available: !emailExists,
        message: emailExists ? 'Email is token!' : 'Email is available'
      };
    } catch (error) {
      return handleError(error, 'checkEmailAvailabilityAdmin');
    }
  },

  /**
   * Check if username is available (admin version - checks against all users)
   * @param {string} username - Username to check
   * @returns {Promise<Object>} API response with availability status
   */
  async checkUsernameAvailabilityAdmin(username) {
    try {
      if (!username || typeof username !== 'string') {
        return { success: false, message: 'Invalid username provided' };
      }

      const trimmedUsername = username.trim().toLowerCase();
      
      // Basic username validation
      if (trimmedUsername.length < 3) {
        return { success: false, message: 'Username must be at least 3 characters long' };
      }

      // Get all users and check locally
      const allUsersResponse = await this.getAllUsers();
      if (!allUsersResponse.success) {
        return allUsersResponse;
      }

      const users = allUsersResponse.data;
      const usernameExists = users.some(user => 
        user.username && 
        user.username.trim().toLowerCase() === trimmedUsername
      );

      return { 
        success: true, 
        available: !usernameExists,
        message: usernameExists ? 'Username is already taken' : 'Username is available'
      };
    } catch (error) {
      return handleError(error, 'checkUsernameAvailabilityAdmin');
    }
  }
};

// Export the PocketBase instance and service methods
export {
  pb,
  pbAdmin,
  ADMIN_AUTH_STORAGE_KEY,
  authService,
  userService,
  socialLinksService,
  favoritesService,
  adminService,
  resolveAuthenticatedRecordId
};

// Default export for backward compatibility
export default pb;